import { request } from "../../api";
import { headers } from "../../../../config/config";
import { MESSAGE } from "../../../../constants/api/message";
import { AUTHORIZATION } from "../../../../constants/api/auth";
import { StatusCodes } from "http-status-codes";
import {
	HealthCardAssetKeysResult,
	HealthCardConfigPayload,
	HealthCardPreviewPayload
} from "../../../../@types/healthCardConfig.types";

const { post, get, put } = request;
const { Authorization, Bearer } = AUTHORIZATION;

// GET/POST/PUT /api/v1/member-portal-UI/health-card-config[/asset-keys|/preview]
// — all groupOwnerAuth-gated (super_admin/admin/employer), unlike the sibling
// /theme and /logo-settings routes, which have no auth at all.
const initialRoute = "member-portal-UI/health-card-config";

const authHeaders = () => ({
	...headers,
	[Authorization]: `${Bearer} ${localStorage.getItem("@jwt")}`
});

// Same shape as alertSubscriptionManagement.ts's error handling: a 400/403
// means the backend already produced a user-facing message (validation
// errors, cross-tenant guard, duplicate-group guard) — surface it and stop;
// anything else is unexpected and should propagate.
const handleKnownErrors = (error: any) => {
	if (error?.response?.status === StatusCodes.BAD_REQUEST || error?.response?.status === StatusCodes.FORBIDDEN) {
		const { message, errors } = error.response.data ?? {};
		alert(errors?.length ? `${message}: ${errors.join(", ")}` : message);
		return;
	}
	throw error;
};

export const getHealthCardConfigs = async (params: { group_number?: number } = {}) => {
	try {
		const response = await get(initialRoute, authHeaders(), params);
		if (response) {
			const {
				data: { message }
			} = response;
			if (message === MESSAGE.get.succ) {
				const {
					data: { result }
				} = response;
				return result as HealthCardConfigPayload[];
			}
		}
		return [];
	} catch (error: any) {
		handleKnownErrors(error);
		return [];
	}
};

export const addHealthCardConfig = async (payload: HealthCardConfigPayload) => {
	try {
		const response = await post(initialRoute, payload, authHeaders());
		if (response) {
			const {
				data: { message }
			} = response;
			if (message === MESSAGE.post.succ) {
				const {
					data: { result }
				} = response;
				return result as HealthCardConfigPayload;
			}
		}
		return undefined;
	} catch (error: any) {
		handleKnownErrors(error);
		return undefined;
	}
};

// Full-document replace — matches the backend, which has no partial-patch
// shape for the nested recursive section tree.
export const editHealthCardConfig = async (payload: HealthCardConfigPayload) => {
	try {
		const response = await put(initialRoute, payload, authHeaders());
		if (response) {
			const {
				data: { message }
			} = response;
			if (message === MESSAGE.put.succ) {
				const {
					data: { result }
				} = response;
				return result as HealthCardConfigPayload;
			}
		}
		return undefined;
	} catch (error: any) {
		handleKnownErrors(error);
		return undefined;
	}
};

// group_number is optional but should be passed whenever known — it also
// resolves that group's real employer_logo URL (from LogoSettings) in the
// same response, so a picker can preview the reserved `employer_logo` asset
// key without a second round trip.
export const getHealthCardAssetKeys = async (group_number?: number) => {
	try {
		const endpoint = `${initialRoute}/asset-keys`;
		const response = await get(endpoint, authHeaders(), group_number ? { group_number } : {});
		if (response) {
			const {
				data: { message }
			} = response;
			if (message === MESSAGE.get.succ) {
				const {
					data: { result }
				} = response;
				return result as HealthCardAssetKeysResult;
			}
		}
		return undefined;
	} catch (error: any) {
		handleKnownErrors(error);
		return undefined;
	}
};

// Renders an unsaved draft through the real buildHealthCardHtml — returns a
// full HTML string meant for an iframe's srcdoc, not a JSON model.
export const previewHealthCardConfig = async (payload: HealthCardPreviewPayload) => {
	try {
		const endpoint = `${initialRoute}/preview`;
		const response = await post(endpoint, payload, authHeaders());
		if (response) {
			const {
				data: { message }
			} = response;
			if (message === MESSAGE.get.succ) {
				const {
					data: { result }
				} = response;
				return result as string;
			}
		}
		return undefined;
	} catch (error: any) {
		handleKnownErrors(error);
		return undefined;
	}
};

export const healthCardConfig = {
	getHealthCardConfigs,
	addHealthCardConfig,
	editHealthCardConfig,
	getHealthCardAssetKeys,
	previewHealthCardConfig
};
